import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { AppConfigService } from '../../shared/appconfig/appconfig.service';
import { ProfileService } from '../profile/profile.service';
import { TenantInfo } from '../../shared/models/tenant.model';

@Injectable({
  providedIn: 'root',
})
export class TenantImpersonationService {
  constructor(
    private http: HttpClient,
    private oidcSecurityService: OidcSecurityService,
    private injector: Injector
  ) {}

  /**
   * ProfileService is resolved on demand rather than constructor-injected.
   *
   * CHAT-959 made MenuService depend on this service (to drop SuperAdmin-only nav
   * while impersonating), and ProfileService already depends on MenuService. Taking
   * ProfileService in the constructor would close that ring and Angular would refuse
   * to build the injector. Nothing here needs a profile at construction time — only
   * the two privilege checks below do, and by then the graph is built.
   */
  private get profileService(): ProfileService {
    return this.injector.get(ProfileService);
  }

  isSuperAdmin(): boolean {
    return this.profileService.hasPrivilege('SuperAdmin');
  }

  loadTenants(): Observable<TenantInfo[]> {
    return this.http.get<TenantInfo[]>(
      AppConfigService.settings.get('chatstyleapiServer') + 'tenants'
    );
  }

  switchTenant(databaseName: string): void {
    if (!this.profileService.hasPrivilege('SuperAdmin')) {
      console.error('Tenant impersonation requires SuperAdmin privilege');
      return;
    }
    this.oidcSecurityService.authorize(null, {
      customParams: {
        provider: 'ChatLocal',
        hostname: 'chat',
        action: 'chat-impersonate',
        target_tenant: databaseName,
      },
    });
  }

  stopImpersonation(): void {
    const originalTenant = this.getOriginalTenant();
    this.oidcSecurityService.authorize(null, {
      customParams: {
        provider: 'ChatLocal',
        hostname: 'chat',
        action: 'chat-stop-impersonation',
        target_tenant: originalTenant || '',
      },
    });
  }

  isImpersonating(): boolean {
    const claims = this.getClaims();
    return !!claims?.impersonator_id;
  }

  getOriginalTenant(): string | null {
    const claims = this.getClaims();
    return claims?.original_tenant ?? null;
  }

  getCurrentTenant(): string | null {
    const claims = this.getClaims();
    return claims?.tenant ?? null;
  }

  private getClaims(): any {
    let claims: any = {};
    this.oidcSecurityService.userData$.pipe(take(1)).subscribe(
      ({ userData }) => (claims = userData ?? {})
    );
    return claims;
  }
}
